import React from 'react'
import { useMap } from 'react-leaflet'
import { LatLng } from 'leaflet'
import { MapObject } from 'api/mapview/mapview.pb'
import { DisplayValue } from "components/ui/utils"

interface MapInfoLabelsProps {
    objects: MapObject[]
    visible: boolean
}

interface InfoLabelProps {
    object: MapObject
    position: { x: number; y: number }
}

const InfoLabel: React.FC<InfoLabelProps> = ({ object, position }) => {
    const displayValue = DisplayValue(object.kind, object.description)
    
    return (
        <div
            style={{
                position: 'absolute',
                left: position.x + 15,
                top: position.y - 10,
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                border: '1px solid #ccc',
                borderRadius: '4px',
                padding: '4px 8px',
                fontSize: '12px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                zIndex: 1000,
                maxWidth: '200px',
                pointerEvents: 'none',
                lineHeight: '1.3'
            }}
        >
            <div style={{ fontWeight: 'bold', marginBottom: '2px', color: '#000' }}>
                {object.name}
            </div>
            {displayValue ? (
                <div style={{ color: displayValue.color || '#333' }}>
                    {displayValue.text}
                </div>
            ) : (
                <div style={{ color: '#666', fontSize: '11px' }}>
                    {object.description && (
                        <div style={{ marginTop: '2px' }}>
                            {typeof object.description === 'object' 
                                ? JSON.stringify(object.description).substring(0, 50) + '...'
                                : String(object.description).substring(0, 50) + '...'
                            }
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}

const MapInfoLabels: React.FC<MapInfoLabelsProps> = ({ objects, visible }) => {
    const map = useMap()
    const [positions, setPositions] = React.useState<{ [key: string]: { x: number; y: number } }>({})

    React.useEffect(() => {
        if (!visible) {
            setPositions({})
            return
        }

        const updatePositions = () => {
            const newPositions: { [key: string]: { x: number; y: number } } = {}
            
            objects.forEach(object => {
                if (object.coords) {
                    const latLng = new LatLng(object.coords.x, object.coords.y)
                    const point = map.latLngToContainerPoint(latLng)
                    newPositions[object.id] = { x: point.x, y: point.y }
                }
            })
            
            setPositions(newPositions)
        }

        updatePositions()

        // Обновляем позиции при изменении карты
        map.on('zoom', updatePositions)
        map.on('move', updatePositions)
        map.on('viewreset', updatePositions)

        return () => {
            map.off('zoom', updatePositions)
            map.off('move', updatePositions)
            map.off('viewreset', updatePositions)
        }
    }, [map, objects, visible])

    if (!visible) {
        return null
    }

    return (
        <>
            {objects.map(object => {
                const position = positions[object.id]
                if (!position || !object.coords) return null
                
                return (
                    <InfoLabel
                        key={object.id}
                        object={object}
                        position={position}
                    />
                )
            })}
        </>
    )
}

export default MapInfoLabels